import React, { useCallback, useEffect, useState } from 'react';
import * as Styles from './UnifyBillingCycleSubscriptionHandler.styles';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import {
  IconArrow, AlertV2, Button, Checkbox, IconInfo, Skeleton,
} from 'gatorcomponents';
import { useHistory } from 'react-router';
import useLocale from '@/hooks/useLocale';
import { billingActions } from '@/redux/modules';
import { formatDate } from '@/utils/Formatters/formatDate';
import { formatCurrency } from '@/utils/Formatters/formatCurrency';
import { Tooltip } from '@/pages/common/v1/Tooltip';
import { useIsMobile } from '@/hooks/useIsMobile/useIsMobile';

const UnifyBillingCycleSubscriptionHandler = ({
  setRedirectToUnifyBillingCyclePage,
}) => {
  const isMobile = useIsMobile();

  const [confirmationChecked, setConfirmationChecked] = useState(false);
  const dispatch = useDispatch();
  const history = useHistory();
  const { billing: billingLocale, routes: routesLocale } = useLocale();
  const {
    manageSubscription: manageSubscriptionLocale,
    paymentCycle: paymentCycleLocale,
  } = billingLocale;

  const params = useParams();
  const { subscriptionId, subscriptionType } = params;

  const { items } = useSelector(state => state.billing.subscriptions);
  const { unifyBillingCycle } = useSelector(state => state.billing.subscriptions);
  const unifyBillingCycleFetch = (unifyBillingCycle && unifyBillingCycle.fetch) || null;

  const subscription = items.find(item => String(item.id) === subscriptionId && item.type === subscriptionType) || null;
  const { hostingId } = subscription || {};

  const { loading: fetchIsLoading, status: fetchStatus } = unifyBillingCycleFetch || {};
  const unifyBillingCycleFetchData = (unifyBillingCycleFetch && unifyBillingCycleFetch.data) || null;
  const {
    products,
    newCycle,
    financialAdjustment,
  } = unifyBillingCycleFetchData || {};
  const { proratedCharge, appliedCredit, total } = financialAdjustment || {};

  const unifyBillingCycleSubmit = (unifyBillingCycle && unifyBillingCycle.submit) || null;
  const { invoiceId, loading: submitIsLoading, status: submitStatus } = unifyBillingCycleSubmit || {};

  const hostingProduct = products && products.find(product => product.type === 'hosting');
  const hostingName = hostingProduct ? hostingProduct.productName : '';

  const addonProduct = products && products.find(product => product.type === 'addon');
  const addonName = addonProduct ? addonProduct.productName : '';

  const handleBackButtonClick = useCallback(() => {
    setRedirectToUnifyBillingCyclePage(false);
  }, [setRedirectToUnifyBillingCyclePage]);

  const handleConfirmUnifyBillingCycle = useCallback(() => {
    if (!hostingId) return;
    dispatch(billingActions.subscriptions.unifyBillingCycle.submit.request(hostingId));
  }, [dispatch, hostingId]);

  useEffect(() => {
    if (items && items.length === 0) {
      dispatch(billingActions.subscriptions.request());
    }
  }, [items, dispatch]);

  const clearUnifyBillingCycle = useCallback(() => {
    dispatch(billingActions.subscriptions.unifyBillingCycle.fetch.clear());
    dispatch(billingActions.subscriptions.unifyBillingCycle.submit.clear());
  }, [dispatch]);

  useEffect(() => {
    clearUnifyBillingCycle();
  }, [clearUnifyBillingCycle]);

  useEffect(() => {
    if (!unifyBillingCycleFetchData && hostingId && fetchStatus == null) {
      dispatch(billingActions.subscriptions.unifyBillingCycle.fetch.request(hostingId));
    }
  }, [dispatch, unifyBillingCycleFetchData, hostingId, fetchStatus]);

  useEffect(() => {
    if (submitStatus === 'success' && invoiceId) {
      history.push(`${routesLocale.invoices}${routesLocale.unpaid}/${invoiceId}?openInvoice=true`);
    }
  }, [history, invoiceId, routesLocale.billing, routesLocale.invoices, routesLocale.unpaid, submitStatus]);

  if (fetchIsLoading || !unifyBillingCycleFetchData) {
    return (
      <Styles.SkeletonWrapper data-testid="unify-billing-cycle-subscription-wrapper-loading">
        <Skeleton width="100%" height="180px" />
        <Skeleton width="100%" height="260px" />
        <Skeleton width="100%" height="500px" />
      </Styles.SkeletonWrapper>
    );
  }

  return (
    <Styles.Wrapper data-testid="unify-billing-cycle-subscription-wrapper">

      <Styles.Header data-testid="unify-billing-cycle-about">
        <Styles.HeaderBackButton onClick={handleBackButtonClick} data-testid="unify-billing-cycle-about-back-button">
          <IconArrow
            size="lg"
            color="lowDark"
            testId="unify-billing-cycle-about-back-button-icon"
            direction="left"
          />
        </Styles.HeaderBackButton>
        <Styles.HeaderContent>
          <Styles.HeaderTitle data-testid="unify-billing-cycle-about-title">
            {manageSubscriptionLocale.unifyBillingCycle.page.title}
          </Styles.HeaderTitle>
          <Styles.HeaderDescription data-testid="unify-billing-cycle-about-description">
            {manageSubscriptionLocale.unifyBillingCycle.page.description(hostingName, addonName)}
          </Styles.HeaderDescription>
        </Styles.HeaderContent>
      </Styles.Header>

      <Styles.CycleContainer data-testid="unify-billing-cycle-products-section">

        {!isMobile && (
          <>
            <Styles.CycleCol>
              <Styles.CycleHeader>
                <Styles.CycleHeaderCol>
                  <Styles.CycleHeaderSub data-testid="unify-billing-cycle-table-product-title">
                    {manageSubscriptionLocale.unifyBillingCycle.page.table.product}
                  </Styles.CycleHeaderSub>
                </Styles.CycleHeaderCol>
              </Styles.CycleHeader>

              {products && products.map(product => (
                <Styles.CycleBody key={product.productName}>
                  <Styles.CycleBodyCol>
                    <Styles.CycleBodySub data-testid="unify-billing-cycle-product-name">
                      {product.productName}
                    </Styles.CycleBodySub>
                  </Styles.CycleBodyCol>
                </Styles.CycleBody>
              ))}
            </Styles.CycleCol>

            <Styles.CycleCol>
              <Styles.CycleHeader>
                <Styles.CycleHeaderCol>
                  <Styles.CycleHeaderSub data-testid="unify-billing-cycle-table-current-cycle-title">
                    {manageSubscriptionLocale.unifyBillingCycle.page.table.currentCycle}
                  </Styles.CycleHeaderSub>
                  <Styles.CycleHeaderSub data-testid="unify-billing-cycle-table-current-renewal-title">
                    {manageSubscriptionLocale.unifyBillingCycle.page.table.currentRenewal}
                  </Styles.CycleHeaderSub>
                </Styles.CycleHeaderCol>
              </Styles.CycleHeader>

              {products && products.map((product) => {
                const billingCycle = product && product.billingCycle && paymentCycleLocale && paymentCycleLocale[product.billingCycle];
                const billingDueDate = (product && product.dueDate) ? formatDate(product.dueDate) : '-';
                return (
                  <Styles.CycleBody key={product.productName}>
                    <Styles.CycleBodyCol>
                      <Styles.CycleBodySub data-testid="unify-billing-cycle-product-current-cycle">
                        {billingCycle}
                      </Styles.CycleBodySub>
                      <Styles.CycleBodySub data-testid="unify-billing-cycle-product-current-renewal">
                        {billingDueDate}
                      </Styles.CycleBodySub>
                    </Styles.CycleBodyCol>
                  </Styles.CycleBody>
                );
              })}
            </Styles.CycleCol>

            <Styles.CycleColLast>
              <Styles.CycleHeaderLast>
                <Styles.CycleHeaderColLast>
                  <Styles.CycleHeaderSub data-testid="unify-billing-cycle-table-new-cycle-title">
                    {manageSubscriptionLocale.unifyBillingCycle.page.table.newCycle}
                  </Styles.CycleHeaderSub>
                  <Styles.CycleHeaderSub data-testid="unify-billing-cycle-table-new-renewal-title">
                    {manageSubscriptionLocale.unifyBillingCycle.page.table.newRenewal}
                  </Styles.CycleHeaderSub>
                </Styles.CycleHeaderColLast>
              </Styles.CycleHeaderLast>
              {newCycle && (
                <Styles.CycleBodyLast>
                  <Styles.CycleBodyColLast>
                    <>
                      <Styles.CycleBodySub data-testid="unify-billing-cycle-product-new-cycle">
                        {newCycle.billingCycle && paymentCycleLocale && paymentCycleLocale[newCycle.billingCycle]}
                      </Styles.CycleBodySub>
                      <Styles.CycleBodySub data-testid="unify-billing-cycle-product-new-renewal">
                        {newCycle.dueDate ? formatDate(newCycle.dueDate) : '-'}
                      </Styles.CycleBodySub>
                    </>
                  </Styles.CycleBodyColLast>
                </Styles.CycleBodyLast>
              )}
            </Styles.CycleColLast>
          </>
        )}

        {isMobile && (
          <>
            {products && products.map((product) => {
              const billingCycle = product && product.billingCycle && paymentCycleLocale && paymentCycleLocale[product.billingCycle];
              const billingDueDate = (product && product.dueDate) ? formatDate(product.dueDate) : '-';

              return (
                <Styles.ProductCycle key={product.productName}>
                  <Styles.ProductCycleName data-testid="unify-billing-cycle-product-name">
                    {product.productName}
                  </Styles.ProductCycleName>
                  <Styles.ProductCycleInfoWrapper>
                    <Styles.ProductCycleInfo data-testid="unify-billing-cycle-product-current-cycle-info">
                      <Styles.ProductCycleLabel>
                        {manageSubscriptionLocale.unifyBillingCycle.page.table.currentCycle}
                      </Styles.ProductCycleLabel>
                      <Styles.ProductCycleValue>
                        {billingCycle}
                      </Styles.ProductCycleValue>
                    </Styles.ProductCycleInfo>
                    <Styles.ProductCycleInfo data-testid="unify-billing-cycle-product-current-renewal-info">
                      <Styles.ProductCycleLabel>
                        {manageSubscriptionLocale.unifyBillingCycle.page.table.currentRenewal}
                      </Styles.ProductCycleLabel>
                      <Styles.ProductCycleValue>
                        {billingDueDate}
                      </Styles.ProductCycleValue>
                    </Styles.ProductCycleInfo>
                  </Styles.ProductCycleInfoWrapper>
                </Styles.ProductCycle>
              );
            })}

            {newCycle && (
              <Styles.ProductCycleUnify>
                <Styles.ProductCycleName data-testid="unify-billing-cycle-product-name">
                  {products && products.map(product => product.productName).join(' + ')}
                </Styles.ProductCycleName>
                <Styles.ProductCycleInfoWrapper>
                  <Styles.ProductCycleInfo data-testid="unify-billing-cycle-product-current-cycle-info">
                    <Styles.ProductCycleLabel>
                      {manageSubscriptionLocale.unifyBillingCycle.page.table.newCycle}
                    </Styles.ProductCycleLabel>
                    <Styles.ProductCycleValue>
                      {newCycle.billingCycle && paymentCycleLocale && paymentCycleLocale[newCycle.billingCycle]}
                    </Styles.ProductCycleValue>
                  </Styles.ProductCycleInfo>
                  <Styles.ProductCycleInfo data-testid="unify-billing-cycle-product-current-renewal-info">
                    <Styles.ProductCycleLabel>
                      {manageSubscriptionLocale.unifyBillingCycle.page.table.newRenewal}
                    </Styles.ProductCycleLabel>
                    <Styles.ProductCycleValue>
                      {newCycle.dueDate ? formatDate(newCycle.dueDate) : '-'}
                    </Styles.ProductCycleValue>
                  </Styles.ProductCycleInfo>
                </Styles.ProductCycleInfoWrapper>
              </Styles.ProductCycleUnify>
            )}
          </>
        )}

      </Styles.CycleContainer>

      <Styles.FinancialSection data-testid="unify-billing-cycle-financial-adjustments-section">
        <Styles.FinancialTitle data-testid="unify-billing-cycle-financial-adjustments-title">
          {manageSubscriptionLocale.unifyBillingCycle.page.financial.title}
        </Styles.FinancialTitle>

        {(proratedCharge || appliedCredit || total) && (
          <Styles.FinancialPaymentItems data-testid="unify-billing-cycle-financial-adjustments-payment-items">

            {proratedCharge && (
            <Styles.FinancialPaymentItem data-testid="unify-billing-cycle-financial-adjustments-payment-charge">
              <Styles.FinancialPaymentText data-testid="unify-billing-cycle-financial-adjustments-payment-charge-text">
                {manageSubscriptionLocale.unifyBillingCycle.page.financial.payment.proratedCharge}
                <Tooltip content={manageSubscriptionLocale.unifyBillingCycle.page.financial.payment.tooltip} preferredPosition="right"><IconInfo color="lowDark" /></Tooltip>
              </Styles.FinancialPaymentText>
              <Styles.FinancialPaymentValue data-testid="unify-billing-cycle-financial-adjustments-payment-charge-value">
                {formatCurrency(proratedCharge)}
              </Styles.FinancialPaymentValue>
            </Styles.FinancialPaymentItem>
            )}

            {appliedCredit && (
            <Styles.FinancialPaymentItem data-testid="unify-billing-cycle-financial-adjustments-payment-credit">
              <Styles.FinancialPaymentText data-testid="unify-billing-cycle-financial-adjustments-payment-credit-text">
                {manageSubscriptionLocale.unifyBillingCycle.page.financial.payment.credit}
              </Styles.FinancialPaymentText>
              <Styles.FinancialPaymentValue data-testid="unify-billing-cycle-financial-adjustments-payment-credit-value">
                -
                {formatCurrency(appliedCredit)}
              </Styles.FinancialPaymentValue>
            </Styles.FinancialPaymentItem>
            )}

            {total && (
            <Styles.FinancialPaymentItem data-testid="unify-billing-cycle-financial-adjustments-payment-total">
              <Styles.FinancialPaymentText data-testid="unify-billing-cycle-financial-adjustments-payment-total-text">
                {manageSubscriptionLocale.unifyBillingCycle.page.financial.payment.total}
              </Styles.FinancialPaymentText>
              <Styles.FinancialPaymentValue data-testid="unify-billing-cycle-financial-adjustments-payment-total-value">
                {formatCurrency(total)}
              </Styles.FinancialPaymentValue>
            </Styles.FinancialPaymentItem>
            )}
          </Styles.FinancialPaymentItems>
        )}

        <Styles.FinancialWarning>
          <AlertV2
            title={manageSubscriptionLocale.unifyBillingCycle.page.financial.warnings.title}
            description={manageSubscriptionLocale.unifyBillingCycle.page.financial.warnings.description}
            testId="unify-billing-cycle-financial-adjustments-warning"
            variant="info"
            width="full"
            withoutBackground={false}
          />
        </Styles.FinancialWarning>

        <Styles.FinancialConfirmation data-testid="unify-billing-cycle-financial-adjustments-confirmation">
          <Styles.FinancialConfirmationCheckboxLabel
            key="unify-confirmation"
            htmlFor="#unify-billing-cycle-financial-adjustments-confirmation-checkbox"
            data-testid="checkUnify-billing-cycle-financial-adjustments-confirmation-checkbox"
            onClick={() => setConfirmationChecked(!confirmationChecked)}
          >
            <Checkbox checked={confirmationChecked} id="unify-billing-cycle-financial-adjustments-confirmation-checkbox" />
            <Styles.FinancialConfirmationText>
              {manageSubscriptionLocale.unifyBillingCycle.page.financial.confirmation}
            </Styles.FinancialConfirmationText>
          </Styles.FinancialConfirmationCheckboxLabel>
        </Styles.FinancialConfirmation>

        <Styles.FinancialButtons data-testid="unify-billing-cycle-financial-adjustments-buttons">
          <Button
            label={manageSubscriptionLocale.unifyBillingCycle.page.financial.buttons.confirm}
            onClick={handleConfirmUnifyBillingCycle}
            variant="primary"
            size="large"
            width={isMobile ? 'full' : 'fit'}
            disabled={!confirmationChecked}
            loading={submitIsLoading}
            testId="unify-billing-cycle-confirm-button"
          />
          <Button
            label={manageSubscriptionLocale.unifyBillingCycle.page.financial.buttons.cancel}
            onClick={handleBackButtonClick}
            variant="tertiary"
            size="large"
            width={isMobile ? 'full' : 'fit'}
            testId="unify-billing-cycle-cancel-button"
          />
        </Styles.FinancialButtons>
      </Styles.FinancialSection>
    </Styles.Wrapper>
  );
};

export default UnifyBillingCycleSubscriptionHandler;
